import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { getCurrentStaff, StaffUser } from "@/Calls/callStaffAuth";
import { Car, Wrench, Clock, CheckCircle, ArrowRight, Loader2, Calendar } from "lucide-react";
import { API_URL } from "@/config";
import { Card } from "@/components/ui/card";

interface ShopStats {
    open: number;
    inProgress: number;
    completedToday: number;
    apptToday: number;
}

interface RepairOrder {
    ro_id: number;
    status: string;
    vehicle_desc: string | null;
    total_amount: string | null;
    created_at: string;
    appointment: { customer_name: string | null; appt_date: string | null; service_type: string | null } | null;
}

const STATUS_COLOR: Record<string, string> = {
    OPEN:        "bg-blue-100 text-blue-700",
    IN_PROGRESS: "bg-orange-100 text-orange-700",
    COMPLETED:   "bg-teal-100 text-teal-700",
    INVOICED:    "bg-violet-100 text-violet-700",
    PAID:        "bg-green-100 text-green-700",
};

export default function CollisionDashboard() {
    const [staff,   setStaff]   = useState<StaffUser | null>(null);
    const [stats,   setStats]   = useState<ShopStats | null>(null);
    const [orders,  setOrders]  = useState<RepairOrder[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            getCurrentStaff().then((r) => { if (r.ok && r.staff) setStaff(r.staff); }),
            axios.get(`${API_URL}/repair-shops/staff/stats`, { withCredentials: true })
                .then((r) => setStats(r.data.stats ?? null))
                .catch(() => null),
            axios.get(`${API_URL}/repair-shops/staff/repair-orders`, { withCredentials: true })
                .then((r) => setOrders((r.data.repair_orders ?? []).slice(0, 6)))
                .catch(() => null),
        ]).finally(() => setLoading(false));
    }, []);

    const statCards = [
        { label: "Active Jobs",     value: stats?.open ?? 0,          icon: Car,         color: "text-blue-500",   bg: "bg-blue-50"   },
        { label: "In Repair",       value: stats?.inProgress ?? 0,    icon: Wrench,      color: "text-orange-500", bg: "bg-orange-50" },
        { label: "Appts Today",     value: stats?.apptToday ?? 0,     icon: Calendar,    color: "text-yellow-500", bg: "bg-yellow-50" },
        { label: "Closed Today",    value: stats?.completedToday ?? 0, icon: CheckCircle, color: "text-green-500",  bg: "bg-green-50"  },
    ];

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold">
                    {staff ? `Welcome, ${staff.first_name}` : "Collision & Body Dashboard"}
                </h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                    {staff ? `Collision & Body · ${staff.role.replace(/_/g, " ")}` : "Loading…"}
                </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {statCards.map(({ label, value, icon: Icon, color, bg }) => (
                    <Card key={label} className="p-4">
                        <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center mb-3`}>
                            <Icon className={`h-5 w-5 ${color}`} />
                        </div>
                        <p className="text-2xl font-bold">
                            {loading ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> : value}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
                    </Card>
                ))}
            </div>

            <div className="flex flex-wrap gap-3">
                <Link
                    to="/collision/repair-orders"
                    className="inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium hover:bg-muted transition"
                >
                    <Wrench className="h-4 w-4" /> Repair Orders
                </Link>
                <Link
                    to="/collision/appointments"
                    className="inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium hover:bg-muted transition"
                >
                    <Clock className="h-4 w-4" /> Appointments
                </Link>
            </div>

            <Card className="overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b">
                    <h2 className="font-semibold">Recent Jobs</h2>
                    <Link
                        to="/collision/repair-orders"
                        className="text-xs text-purple-600 hover:underline flex items-center gap-1"
                    >
                        View all <ArrowRight className="h-3 w-3" />
                    </Link>
                </div>

                {loading ? (
                    <div className="flex justify-center py-10">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                ) : orders.length === 0 ? (
                    <div className="py-10 text-center text-sm text-muted-foreground">No repair orders yet</div>
                ) : (
                    <div className="divide-y">
                        {orders.map((ro) => (
                            <Link
                                key={ro.ro_id}
                                to={`/collision/repair-orders/${ro.ro_id}`}
                                className="flex items-center justify-between px-5 py-3 hover:bg-muted/40 transition-colors"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center">
                                        <Car className="h-4 w-4 text-purple-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium">RO #{ro.ro_id}</p>
                                        <p className="text-xs text-muted-foreground">
                                            {ro.vehicle_desc ?? ro.appointment?.service_type ?? "—"}
                                        </p>
                                        {ro.appointment?.customer_name && (
                                            <p className="text-xs text-muted-foreground">{ro.appointment.customer_name}</p>
                                        )}
                                    </div>
                                </div>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLOR[ro.status] ?? "bg-gray-100 text-gray-600"}`}>
                                    {ro.status.replace("_", " ")}
                                </span>
                            </Link>
                        ))}
                    </div>
                )}
            </Card>
        </div>
    );
}
